import { Api, ModelRow, StringUtil } from "@mcleod/core";
import { BlurEvent, LookupModelSelectionEvent, Panel, TableRow, TableRowBeforeSaveEvent, TableRowDisplayEvent, Textbox } from "@mcleod/components";
import { getDispatchControlBoolean, getDispatchControlValue, LMEGeneralSettings } from "@mcleod/general";
import { AutogenLayoutMovementRouting } from "./autogen/AutogenLayoutMovementRouting";
import { EquipmentGroupValidation, EquipmentGroupValidationResult } from "./EquipmentGroupValidation";
import { CommonTooltips } from "@mcleod/common";
import { PlanningUtil } from "../planning/PlanningUtil";
import { UnassignDialog } from "../UnassignDialog";

export class MovementRouting extends AutogenLayoutMovementRouting {
    public afterEquipmentValidation: (result:EquipmentGroupValidationResult) => void = () => { };

    /** This is an event handler for the onRowDisplay event of tableMovementRouting. */
    tableMovementRoutingOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        const modelRow = tableRow.data;
        const maximumDrivers = getDispatchControlValue("maximum_drivers");
        const maximumTrailers = getDispatchControlValue("maximum_trailers");
        const typeSequence = parseInt(modelRow.get("type_sequence"));
        const panelTractorInfo = tableRow.findComponentById("panelTractorInfo") as Panel;
        if (typeSequence != 0) panelTractorInfo.visible = false;

        const textboxDriverId = tableRow.findComponentById("textboxDriverId") as Textbox;
        textboxDriverId.caption = `Driver ${typeSequence == 0 ? "1" : "2"} Name`;

        const textboxDollyNumber = tableRow.findComponentById("textboxDollyNumber") as Textbox;
        const textboxTrailerNumber = tableRow.findComponentById("textboxTrailerNumber") as Textbox;
        const panelTrailerDolly = tableRow.findComponentById("panelTrailerDolly") as Panel;
        const panelDriver = tableRow.findComponentById("panelDriver") as Panel;
        const panelPTA = tableRow.findComponentById("panelPTA") as Panel;
        if (typeSequence >= maximumDrivers) {
            textboxDriverId.visible = false;
            panelPTA.visible = false;
            panelDriver.minWidth = Number(panelDriver.width) + Number(panelPTA.minWidth);
        }

        if (typeSequence >= maximumTrailers) {
            panelTrailerDolly.visible = false;
            panelPTA.borderRightWidth = 0;
        } else {
            const dollyLabels = ["1", "2"];
            textboxDollyNumber.caption = `Dolly ${dollyLabels[typeSequence - 1] || (typeSequence).toString()}`;
            if (typeSequence === 0 ) {
                textboxDollyNumber.visible = false;
            }

            const trailerLabels = ["1", "2", "3"];
            textboxTrailerNumber.caption = `Trailer ${trailerLabels[typeSequence] || (typeSequence + 1).toString()}`;
        }

        const textboxTractorNumber = tableRow.findComponentById("textboxTractorNumber") as Textbox;

        const panelReadOnlyRouting = tableRow.findComponentById("panelReadOnlyRouting");
        if (panelReadOnlyRouting && "D" == this.mainDataSource.parentDataSource.activeRow.getString("status")) {
            panelReadOnlyRouting.visible = false;
        }

        this.setupPTA(tableRow);
    }

    /** This is an event handler for the beforeRowSave event of tableMovementRouting. */
    tableMovementRoutingBeforeRowSave(event: TableRowBeforeSaveEvent) {
        const tableRow = event.getTableRow();
        const modelRow = tableRow.data;
        modelRow._appending = true; // For some reason, we create new equipment item records each we make an update so it'll always be appending
    }

    async onEquipmentSelect(event: LookupModelSelectionEvent) {
        const textbox = event.target as Textbox;
        const tableRow = TableRow.getContainingTableRow(textbox) as TableRow;
        const modelRow = tableRow.data;

        // Collect individual trailer IDs from the equipment group
        const maximumTrailers = getDispatchControlValue("maximum_trailers");
        let trailer2Id: string | null = null;
        let trailer3Id: string | null = null;

        if (this.sourceEquipmentGroup.data.length > 1 && maximumTrailers >= 2) {
            trailer2Id = this.sourceEquipmentGroup.data[1].get("trailer_id", null);
        }
        if (this.sourceEquipmentGroup.data.length > 2 && maximumTrailers >= 3) {
            trailer3Id = this.sourceEquipmentGroup.data[2].get("trailer_id", null);
        }

        const validationResult = await EquipmentGroupValidation.validate({
            checkInProgress: this.mainDataSource.parentDataSource.activeRow.get("status") == "P",
            preassignment: this.mainDataSource.parentDataSource.activeRow.get("status") == "A",
            movementId: this.mainDataSource.parentDataSource.activeRow.get("id"),
            modelRow, field: textbox.field,
            tractorId: this.activeRow.get("tractor_id", null),
            driverId: this.activeRow.get("driver_id", null),
            ...(this.sourceEquipmentGroup.data.length > 1 ?
                { driver2Id: this.sourceEquipmentGroup.data[1].get("driver_id", null) }
                : {}),
            trailerId: this.sourceEquipmentGroup.data[0].get("trailer_id", null),
            trailer2Id: trailer2Id,
            trailer3Id: trailer3Id
        });

        const unassignDialogSuccess = (validationResult.errors.length == 0)
            ? await this.checkForUnassigned(textbox.field, modelRow)
            : true;

        // Only redisplay if the row is one of the first two (driver one or driver two) since those are the only ones that can affect other rows
        if (!modelRow.isNull(textbox.field) && tableRow.index < 2)
            this.tableMovementRouting.redisplaySingleRow(tableRow.index, modelRow, false, true);
        this.afterEquipmentValidation(validationResult);
    }

    calculateRegulatoryIssuePanelHeight(items: any[], rowLength: number, lineHeight: number, blankSpace: number) : number {
        let calcHeight = 0;
        calcHeight += blankSpace;

        items?.forEach(item => {
            calcHeight += (((Math.floor(item.length / rowLength))+1) * lineHeight) ;
            }
        );
        return calcHeight;
    }

    createPanel(id: string): Panel {
        return new Panel({ id });
    }

    async equipmentOnBlur(event: BlurEvent) {
        const textbox = event.target as Textbox;
        const tableRow = TableRow.getContainingTableRow(textbox) as TableRow;
        if (textbox.isDropdownVisible() || textbox.validationWarning != null) return;
        const modelRow = textbox.getRelevantModelRow();
        if (StringUtil.isEmptyString(modelRow.get(textbox.field))) {
            await this.checkForUnassigned(textbox.field, modelRow);
        }
        this.tableMovementRouting.redisplaySingleRow(this.sourceEquipmentGroup.findRowInData(modelRow), modelRow, false, true);
    }

    async checkForUnassigned(field: string, modelRow: ModelRow) {
        if (modelRow.originalData
        && !StringUtil.isEmptyString(modelRow.originalData[field])
        && !StringUtil.isEmptyString(modelRow.data[field])
        && modelRow.originalData[field] != modelRow.data[field]
        && getDispatchControlBoolean("req_unassign_reason", false)) {
            const resultRow = await new UnassignDialog("B").show();
            if (resultRow) {
                this.activeRow.set("unassign_reason", resultRow);
                if (field === "tractor_id" && StringUtil.isEmptyString(modelRow.get(field))) { // clear driver as well
                    this.activeRow.set("driver_id", null);
                }
            } else {
                modelRow.revertToOriginalData(field);
                if (field === "tractor_id") {
                    modelRow.revertToOriginalData("driver_id");
                    modelRow.revertToOriginalData("driver_two_id");
                }
                return false;
            }
        }
        return true;
    }

    getEquipmentId() {
        const dispatchBy = getDispatchControlValue("dispatch_by");
        if (dispatchBy === "T") {
            return this.activeRow?.get("tractor_id", null) ?? null;
        } else {
            return this.activeRow?.get("driver_id", null) ?? null;
        }
    }

    setupPTA(tableRow: TableRow){
        const data = tableRow.data as ModelRow;
        const onSave = async () => {
            const driverId: string = data.get("driver_id");
            await Api.post("lme/dispatch/asset/update-driver-pta",
                {driver_id: driverId, pta_date: data.getDateTime("pta"), is_manual: true});
        }

        const textbox = tableRow.findComponentById("textboxPTA") as Textbox;
        textbox.printable = true;
        if(!data.isNull("pta")){
            const dispatchControlSettings = LMEGeneralSettings.getSingleton().getDispatchControl();
            if ((dispatchControlSettings.record_pta == "Y" || dispatchControlSettings.record_pta_web == "Y") && data.getBoolean("pta_manual", false)){
                PlanningUtil.addTextboxEditability({
                    textbox:textbox,
                    idText:"driverPTA",
                    tableRow:null,
                    onSave: onSave,
                    useWrapper: false
                });
            }
        }
    }
}
